import { Observable } from "rxjs";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";
import { useEvent$ } from "./useEventListener";
import { useMutation$ } from "./useMutation";
import { useSessionStorage } from "./useSessionStorage";
import { clamp, State } from "@zyzgroup/core-common";

export function useLocation$(): Observable<Location> {
  return useMutation$(document).pipe(
    map(() => location), // const { pathname, search } = location;
    startWith(location)
  );
}

export function useUrlChange$(): Observable<string> {
  return useLocation$().pipe(
    map((location) => location.href),
    startWith(location.href),
    distinctUntilChanged()
  );
}

export function useHashChange$(): Observable<string> {
  return useEvent$(window, "hashchange").pipe(
    map((hashEvent: HashChangeEvent) =>
      (hashEvent.target as Window).location.hash.replace("#", "")
    ),
    startWith(window.location.hash.replace("#", "")),
    distinctUntilChanged()
  );
}

// 浏览器维护一个“历史记录栈”，每访问一个页面或状态，就像往栈里“压入”一条记录 = 浏览器的“时间机”
// - 用户点击浏览器的 前进/后退 按钮，实际上就是在这个栈里移动指针
// - 当前页面 = 栈中的当前指针位置
// - history.back() / history.go(-1) = 指针向前一个页面移动，相当于用户点击浏览器“后退”按钮
// - history.forward() / history.go(1) = 指针向下一个页面移动，相当于用户点击浏览器“前进”按钮

// window.history.pushState(state, title = "", url)
// - 在不刷新页面的情况下向历史记录栈中“压入新状态”
// - URL 地址栏更新为 url
// - 页面内容不会自动刷新
// - pushState 不会触发 popstate
// - 如果有多个源头对同一个url进行 pushState 操作，浏览器会合并多个源头的 state

// window.history.replaceState(state, title = "", url)
// - 在不刷新页面的情况下向历史记录栈中“替换新状态”
// - 页面内容不会自动刷新
// - replaceState 不会触发 popstate

// SPA 历史管理示意图
// 历史栈: [page1] -> [page2] -> [page3]
//           ↑       ↑        ↑
//           |       |        |
//         当前指针   pushState   pushState
// 操作流程：
// 1. 初始状态：当前指针指向 page1
// 2. window.history.pushState({page:2}, "", "/page2")
//    -> 栈中新增 page2，指针移动到 page2
//    -> 不触发 popstate
// 3. window.history.pushState({page:3}, "", "/page3")
//    -> 栈中新增 page3，指针移动到 page3
//    -> 不触发 popstate
// 4. window.history.back()
//    -> 指针回到 page2
//    -> 触发 popstate, event.state = {page:2}
// 5. window.history.back()
//    -> 指针回到 page1
//    -> 触发 popstate, event.state = {page:1}
// 6. window.history.forward()
//    -> 指针前进到 page2
//    -> 触发 popstate, event.state = {page:2}

// nameInState = state 中用于判断的 当前url 的字段名称，例如 vue-router 的 pop-state = {
//   back: "/",
//   current: "/lot?n=739",
//   forward: "/result",
//   position: 18,
//   replaced: false,
//   scroll: {left: 0, top: 0}
// }
// 监听：浏览器历史栈指针变化：
// - 用户点击浏览器 后退/前进 按钮
// - 或 history.back() / history.forward() / history.go(-1) / history.go(1) 时触发的 popstate 事件
export function usePopStateChange$(nameInState = "current"): Observable<any> {
  return useEvent$(window, "popstate").pipe(
    // 注意：当页面刷新时，history.state（最近一次 pushState/replaceState 的 state对象） 可能为 null
    // 即：浏览器可能不会自动恢复 state 对象
    // startWith(null),
    map((popStateEvent: PopStateEvent) => popStateEvent.state || {}),
    map((state) => ({
      ...state,
      // pop-state 转换为 标准输出-state
      [nameInState]:
        state[nameInState] ||
        window.location.pathname + window.location.search + window.location.hash
    })),
    distinctUntilChanged((a, b) => a[nameInState] === b[nameInState])
  );
  // window.dispatchEvent(new PopStateEvent("name", { state }));
}

// 问题现象描述
// 1. 用户在页面 A → 页面 B → 页面 C 进行了多次导航
// 2. 浏览器刷新页面 C
// 3. 浏览器的 返回按钮 仍可用，说明浏览器的原生 session history 还在
// 4. 但是在本地维护的访问记录（比如 JS 的栈或历史数组）被清空了
// 5. 结果导致 SPA 的页面切换动画逻辑认为是 push 操作（forward），而不是 back 操作，所以动画不符合预期
// 解决方案：动画方向应基于 浏览器历史记录，而非内存栈

export type FeRouteDirection = "forward" | "backward" | "keep";

/**
 *
 * @param nameInState state 中用于判断的 当前url 的字段名称
 * @param alreadyPushState 是否第三方库已自动 pushState，例如 Vue-Router 已自动操作 pushState
 * @returns
 */
export function useFeRoute(
  options: { nameInState: string; alreadyPushState?: boolean } = {
    nameInState: "current",
    alreadyPushState: true
  }
) {
  const nameInState = options.nameInState;
  const alreadyPushState =
    typeof options.alreadyPushState == "boolean"
      ? options.alreadyPushState
      : true;

  type FeRouteState = { [nameInState]: string } & State;

  // 历史记录内存栈 初始化为 浏览器历史记录
  const { get: getHistoryInSession, set: setHistoryInSession } =
    useSessionStorage("fe-route-history");
  const history: FeRouteState[] = getHistoryInSession() || [];

  // 当前历史记录栈指针
  const { get: getHistoryIndexInSession, set: setHistoryIndexInSession } =
    useSessionStorage("fe-route-history-index");
  const initIndex = getHistoryIndexInSession();
  let currentIndex =
    typeof initIndex == "number" ? initIndex : history.length - 1;

  function findIndexInHistory(state: FeRouteState) {
    const index = history.findIndex(
      (s) => s[nameInState] === state[nameInState]
    );
    return index;
  }

  // 路由变化
  const route: {
    currentIndex: number;
    to?: string;
    from?: string;
    direction?: FeRouteDirection;
    delta?: number;
  } = { currentIndex };

  function setCurrent(index: number) {
    // console.log("before currentIndex: ", currentIndex);
    index = clamp(index, -1, history.length - 1);
    if (currentIndex < 0) {
      // 无历史记录，第一次进入
      route.direction = "keep";
      route.delta = 0;
    } else {
      if (index < currentIndex) {
        route.direction = "backward";
      } else if (index > currentIndex) {
        route.direction = "forward";
      } else {
        route.direction = "keep";
      }
      route.delta = index - currentIndex;
    }
    route.from = route.to;
    route.to = history[index]?.[nameInState];
    currentIndex = index;
    route.currentIndex = currentIndex;
    setHistoryIndexInSession(currentIndex);
    // console.log("after currentIndex: ", currentIndex);
  }

  // 初始化
  setCurrent(currentIndex);

  function pushHistory(state: FeRouteState) {
    history.push(state);
    setHistoryInSession(history);
    setCurrent(history.length - 1);
  }
  function replaceHistory(state: FeRouteState) {
    const index = findIndexInHistory(state);
    if (index >= 0) {
      history.splice(index, 1, state);
      setHistoryInSession(history);
    } else {
      pushHistory(state);
    }
  }
  function pushState(url: string, origin: State) {
    const state = { ...origin, [nameInState]: url };
    window.history.pushState(state, "", url);
    pushHistory(state);
  }
  function replaceState(url: string, origin: State) {
    const state = { ...origin, [nameInState]: url };
    window.history.replaceState(state, "", url);
    replaceHistory(state);
  }

  // 前进或后退
  function gotoHistory(state: FeRouteState, isPushedState = alreadyPushState) {
    const index = findIndexInHistory(state);
    if (index < 0) {
      // 如果栈中没有，说明是 前进 或 刷新 或 直接访问URL，插入栈
      if (currentIndex >= 0) {
        // 与浏览器行为保持一致：删除部分记录
        history.splice(currentIndex + 1);
        setHistoryInSession(history);
      }
      isPushedState ? pushHistory(state) : pushState(state[nameInState], state);
      return;
    }
    if (index == currentIndex) {
      // 已是当前，忽略
      return;
    }
    // 后退
    // 与浏览器行为保持一致：不删除记录
    setCurrent(index);
  }

  const popstate$ = usePopStateChange$().subscribe((state) => {
    // console.log("popstate", state);
    // 前进或后退
    gotoHistory(state, true);
  });

  return {
    history,
    route,
    // 统一对外接口：前进或后退
    // 注意与 popstate$ 的前后调用顺序
    navigate: (url: string, origin: State) => {
      gotoHistory({ ...origin, [nameInState]: url }, alreadyPushState);
    },
    go(delta: number) {
      window.history.go(delta);
    },
    back() {
      window.history.back();
    },
    forward() {
      window.history.forward();
    },
    destroy: () => {
      popstate$.unsubscribe();
    }
  };
}
